import React, {
    FormEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "react-toastify";
import API_BASE_URL from "../config";
import { useAuth0 } from "@auth0/auth0-react";
import { Contact } from "../models/Contact";
import "./Contacts.css";

type IconName =
    | "add"
    | "search"
    | "edit"
    | "delete"
    | "contacts"
    | "email"
    | "close"
    | "previous"
    | "next";

type IconProps = {
    name: IconName;
    size?: number;
};

function Icon({ name, size = 18 }: IconProps) {
    const paths: Record<IconName, React.ReactNode> = {
        add: (
            <>
                <path d="M12 5v14" />
                <path d="M5 12h14" />
            </>
        ),
        search: (
            <>
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-4-4" />
            </>
        ),
        edit: (
            <>
                <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-4-4L4 16v4Z" />
                <path d="m13.5 6.5 4 4" />
            </>
        ),
        delete: (
            <>
                <path d="M4 7h16" />
                <path d="M9 7V4h6v3" />
                <path d="m7 7 1 13h8l1-13" />
                <path d="M10 11v5" />
                <path d="M14 11v5" />
            </>
        ),
        contacts: (
            <>
                <circle cx="9" cy="8" r="3" />
                <path d="M3.5 19c.5-3.3 2.4-5 5.5-5s5 1.7 5.5 5" />
                <circle cx="17" cy="9" r="2.25" />
                <path d="M15.5 14.5c2.9-.4 4.6 1.1 5 4.5" />
            </>
        ),
        email: (
            <>
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m4 7 8 6 8-6" />
            </>
        ),
        close: (
            <>
                <path d="m6 6 12 12" />
                <path d="m18 6-12 12" />
            </>
        ),
        previous: <path d="m15 18-6-6 6-6" />,
        next: <path d="m9 18 6-6-6-6" />,
    };

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            {paths[name]}
        </svg>
    );
}

function getInitials(name?: string, email?: string): string {
    const source = name?.trim() || email?.trim() || "Contact";

    return source
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
}

async function readErrorMessage(
    response: Response,
    fallback: string
): Promise<string> {
    const text = await response.text();

    if (!text) {
        return fallback;
    }

    try {
        const json = JSON.parse(text);
        return (
            json?.message ||
            json?.detail ||
            json?.title ||
            fallback
        );
    } catch {
        return text;
    }
}

const PAGE_SIZE = 10;

type ApiContact = Partial<Contact> & {
    Id?: string;
    Name?: string;
    Email?: string;
};

function normalizeContact(value: ApiContact): Contact {
    return {
        ...value,
        id: value.id ?? value.Id ?? "",
        name: value.name ?? value.Name ?? "",
        email: value.email ?? value.Email ?? "",
    } as Contact;
}

const ContactsPage: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();

    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<Contact | null>(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [formError, setFormError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const fetchContacts = useCallback(
        async (query?: string) => {
            try {
                setLoading(true);
                setPageError(null);

                const token = await getAccessTokenSilently();
                const trimmedQuery = query?.trim();

                const url = trimmedQuery
                    ? `${API_BASE_URL}/contacts?search=${encodeURIComponent(
                        trimmedQuery
                    )}`
                    : `${API_BASE_URL}/contacts`;

                const response = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(
                        await readErrorMessage(
                            response,
                            "Failed to load contacts."
                        )
                    );
                }

                const data: unknown = await response.json();

                const normalizedContacts = Array.isArray(data)
                    ? data.map((item) => normalizeContact(item as ApiContact))
                    : [];

                setContacts(normalizedContacts);
            } catch (error: any) {
                setContacts([]);
                setPageError(
                    error?.message || "Unable to load contacts."
                );
            } finally {
                setLoading(false);
            }
        },
        [getAccessTokenSilently]
    );

    useEffect(() => {
        void fetchContacts();

        return () => {
            if (searchTimer.current) {
                clearTimeout(searchTimer.current);
            }
        };
    }, [fetchContacts]);

    useEffect(() => {
        const totalPages = Math.max(
            1,
            Math.ceil(contacts.length / PAGE_SIZE)
        );

        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [contacts.length, page]);

    const openAdd = () => {
        setEditing(null);
        setName("");
        setEmail("");
        setFormError(null);
        setShowModal(true);
    };

    const openEdit = (contact: Contact) => {
        const normalizedContact = normalizeContact(contact as ApiContact);

        setEditing(normalizedContact);
        setName(normalizedContact.name ?? "");
        setEmail(normalizedContact.email ?? "");
        setFormError(null);
        setShowModal(true);
    };

    const closeModal = () => {
        if (isSaving) {
            return;
        }

        setShowModal(false);
        setEditing(null);
        setFormError(null);
    };

    const handleSave = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);

        const editingContact = editing
            ? normalizeContact(editing as ApiContact)
            : null;

        // During a name-only edit, preserve the contact's existing email.
        // This also handles APIs that return PascalCase properties.
        const cleanName = (
            name || editingContact?.name || ""
        ).trim();
        const cleanEmail = (
            email || editingContact?.email || ""
        )
            .trim()
            .toLowerCase();

        if (!cleanName || !cleanEmail) {
            setFormError("Name and email are required.");
            return;
        }

        if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
            setFormError("Please enter a valid email address.");
            return;
        }

        const duplicateExists = contacts.some(
            (contact) =>
                contact.email?.trim().toLowerCase() === cleanEmail &&
                (!editing || contact.id !== editing.id)
        );

        if (duplicateExists) {
            const message = "A contact with this email already exists.";
            setFormError(message);
            toast.error(message);
            return;
        }

        const wasEditing = Boolean(editing);

        try {
            setIsSaving(true);

            const token = await getAccessTokenSilently();
            const url = editing
                ? `${API_BASE_URL}/contacts/${editing.id}`
                : `${API_BASE_URL}/contacts`;

            const response = await fetch(url, {
                method: editing ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    Name: cleanName,
                    Email: cleanEmail,
                }),
            });

            if (!response.ok) {
                throw new Error(
                    await readErrorMessage(
                        response,
                        editing
                            ? "Failed to update contact."
                            : "Failed to create contact."
                    )
                );
            }

            await fetchContacts(searchTerm || undefined);
            setShowModal(false);
            setEditing(null);
            setName("");
            setEmail("");
            setFormError(null);

            toast.success(
                wasEditing
                    ? "Contact updated successfully."
                    : "Contact added successfully."
            );
        } catch (error: any) {
            const message =
                error?.message || "Unable to save contact.";
            setFormError(message);
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (contact: Contact) => {
        const confirmed = window.confirm(
            `Delete ${contact.name || contact.email}?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingId(contact.id);
            setPageError(null);

            const token = await getAccessTokenSilently();
            const response = await fetch(
                `${API_BASE_URL}/contacts/${contact.id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    await readErrorMessage(
                        response,
                        "Failed to delete contact."
                    )
                );
            }

            await fetchContacts(searchTerm || undefined);
            toast.success("Contact deleted successfully.");
        } catch (error: any) {
            const message =
                error?.message || "Unable to delete contact.";
            setPageError(message);
            toast.error(message);
        } finally {
            setDeletingId(null);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPage(1);

        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }

        searchTimer.current = setTimeout(() => {
            void fetchContacts(value);
        }, 300);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setPage(1);

        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
        }

        void fetchContacts();
    };

    const pagedContacts = useMemo(
        () =>
            contacts.slice(
                (page - 1) * PAGE_SIZE,
                page * PAGE_SIZE
            ),
        [contacts, page]
    );

    const totalPages = Math.max(
        1,
        Math.ceil(contacts.length / PAGE_SIZE)
    );

    const rangeStart =
        contacts.length === 0
            ? 0
            : (page - 1) * PAGE_SIZE + 1;

    const rangeEnd = Math.min(
        page * PAGE_SIZE,
        contacts.length
    );

    return (
        <div className="contacts-page">
            <section className="contacts-header">
                <div>
                    <span className="contacts-eyebrow">
                        Your address book
                    </span>

                    <h1>Contacts</h1>

                    <p>
                        Save the people you travel with so you can add
                        them to future trips quickly.
                    </p>
                </div>

                <button
                    type="button"
                    className="contacts-primary-button"
                    onClick={openAdd}
                >
                    <Icon name="add" />
                    <span>Add contact</span>
                </button>
            </section>

            <section className="contacts-summary-row">
                <div className="contacts-stat-card">
                    <span className="contacts-stat-icon">
                        <Icon name="contacts" />
                    </span>

                    <div>
                        <span className="contacts-stat-label">
                            Saved contacts
                        </span>
                        <strong>{contacts.length}</strong>
                    </div>
                </div>

                <div className="contacts-search-wrap">
                    <span className="contacts-search-icon">
                        <Icon name="search" />
                    </span>

                    <input
                        type="search"
                        className="contacts-search-input"
                        placeholder="Search by name or email"
                        value={searchTerm}
                        onChange={(event) =>
                            handleSearchChange(event.target.value)
                        }
                        aria-label="Search contacts"
                    />

                    {searchTerm && (
                        <button
                            type="button"
                            className="contacts-search-clear"
                            onClick={clearSearch}
                            aria-label="Clear search"
                        >
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
            </section>

            {pageError && (
                <div className="contacts-alert contacts-alert-error">
                    {pageError}
                </div>
            )}

            <section className="contacts-card">
                <div className="contacts-card-header">
                    <div>
                        <h2>Saved contacts</h2>
                        <p>
                            Add these contacts directly when creating or
                            updating a trip.
                        </p>
                    </div>

                    {!loading && contacts.length > 0 && (
                        <span className="contacts-result-count">
                            {contacts.length}{" "}
                            {contacts.length === 1
                                ? "contact"
                                : "contacts"}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="contacts-loading">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                        >
                            <span className="visually-hidden">
                                Loading contacts...
                            </span>
                        </div>
                        <span>Loading contacts...</span>
                    </div>
                ) : contacts.length === 0 ? (
                    <div className="contacts-empty">
                        <span className="contacts-empty-icon">
                            <Icon name="contacts" size={28} />
                        </span>

                        <h3>
                            {searchTerm
                                ? "No contacts match your search"
                                : "No contacts saved yet"}
                        </h3>

                        <p>
                            {searchTerm
                                ? "Try another name or email address."
                                : "Add your first contact to make future trip invitations faster."}
                        </p>

                        {searchTerm ? (
                            <button
                                type="button"
                                className="contacts-secondary-button"
                                onClick={clearSearch}
                            >
                                Clear search
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="contacts-primary-button"
                                onClick={openAdd}
                            >
                                <Icon name="add" />
                                <span>Add first contact</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="contacts-desktop-table-wrap">
                            <table className="contacts-table">
                                <thead>
                                    <tr>
                                        <th>Contact</th>
                                        <th>Email</th>
                                        <th className="contacts-actions-column">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {pagedContacts.map((contact) => (
                                        <tr key={contact.id}>
                                            <td>
                                                <div className="contacts-person">
                                                    <span className="contacts-avatar">
                                                        {getInitials(
                                                            contact.name,
                                                            contact.email
                                                        )}
                                                    </span>

                                                    <div className="contacts-person-copy">
                                                        <strong>
                                                            {contact.name ||
                                                                "Unnamed contact"}
                                                        </strong>
                                                        <small>
                                                            Saved contact
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>

                                            <td>
                                                <a
                                                    className="contacts-email-link"
                                                    href={`mailto:${contact.email}`}
                                                >
                                                    <Icon
                                                        name="email"
                                                        size={16}
                                                    />
                                                    <span>
                                                        {contact.email}
                                                    </span>
                                                </a>
                                            </td>

                                            <td>
                                                <div className="contacts-row-actions">
                                                    <button
                                                        type="button"
                                                        className="contacts-icon-button"
                                                        onClick={() =>
                                                            openEdit(contact)
                                                        }
                                                        aria-label={`Edit ${contact.name}`}
                                                        title="Edit contact"
                                                    >
                                                        <Icon name="edit" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="contacts-icon-button contacts-icon-button-danger"
                                                        onClick={() =>
                                                            void handleDelete(
                                                                contact
                                                            )
                                                        }
                                                        disabled={
                                                            deletingId ===
                                                            contact.id
                                                        }
                                                        aria-label={`Delete ${contact.name}`}
                                                        title="Delete contact"
                                                    >
                                                        {deletingId ===
                                                            contact.id ? (
                                                            <span className="spinner-border spinner-border-sm" />
                                                        ) : (
                                                            <Icon name="delete" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="contacts-mobile-list">
                            {pagedContacts.map((contact) => (
                                <article
                                    key={contact.id}
                                    className="contacts-mobile-card"
                                >
                                    <div className="contacts-mobile-top">
                                        <span className="contacts-avatar">
                                            {getInitials(
                                                contact.name,
                                                contact.email
                                            )}
                                        </span>

                                        <div className="contacts-mobile-person">
                                            <strong>
                                                {contact.name ||
                                                    "Unnamed contact"}
                                            </strong>

                                            <a
                                                href={`mailto:${contact.email}`}
                                            >
                                                {contact.email}
                                            </a>
                                        </div>
                                    </div>

                                    <div className="contacts-mobile-actions">
                                        <button
                                            type="button"
                                            className="contacts-secondary-button"
                                            onClick={() =>
                                                openEdit(contact)
                                            }
                                        >
                                            <Icon
                                                name="edit"
                                                size={16}
                                            />
                                            <span>Edit</span>
                                        </button>

                                        <button
                                            type="button"
                                            className="contacts-danger-button"
                                            onClick={() =>
                                                void handleDelete(
                                                    contact
                                                )
                                            }
                                            disabled={
                                                deletingId === contact.id
                                            }
                                        >
                                            {deletingId ===
                                                contact.id ? (
                                                <span className="spinner-border spinner-border-sm" />
                                            ) : (
                                                <Icon
                                                    name="delete"
                                                    size={16}
                                                />
                                            )}
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>

                        {contacts.length > PAGE_SIZE && (
                            <div className="contacts-pagination">
                                <span>
                                    Showing {rangeStart}–{rangeEnd} of{" "}
                                    {contacts.length}
                                </span>

                                <div className="contacts-pagination-controls">
                                    <button
                                        type="button"
                                        className="contacts-page-button"
                                        disabled={page <= 1}
                                        onClick={() =>
                                            setPage((current) =>
                                                Math.max(
                                                    1,
                                                    current - 1
                                                )
                                            )
                                        }
                                    >
                                        <Icon
                                            name="previous"
                                            size={16}
                                        />
                                        <span>Previous</span>
                                    </button>

                                    <span className="contacts-page-status">
                                        Page {page} of {totalPages}
                                    </span>

                                    <button
                                        type="button"
                                        className="contacts-page-button"
                                        disabled={page >= totalPages}
                                        onClick={() =>
                                            setPage((current) =>
                                                Math.min(
                                                    totalPages,
                                                    current + 1
                                                )
                                            )
                                        }
                                    >
                                        <span>Next</span>
                                        <Icon name="next" size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>

            {showModal && (
                <div
                    className="contacts-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="contact-modal-title"
                    onMouseDown={closeModal}
                >
                    <div
                        className="contacts-modal"
                        onMouseDown={(event) =>
                            event.stopPropagation()
                        }
                    >
                        <div className="contacts-modal-header">
                            <div>
                                <span className="contacts-modal-icon">
                                    {editing ? (
                                        <Icon name="edit" />
                                    ) : (
                                        <Icon name="add" />
                                    )}
                                </span>

                                <div>
                                    <h2 id="contact-modal-title">
                                        {editing
                                            ? "Edit contact"
                                            : "Add contact"}
                                    </h2>

                                    <p>
                                        {editing
                                            ? "Update this contact’s name or email address."
                                            : "Save someone you regularly travel with."}
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="contacts-modal-close"
                                onClick={closeModal}
                                disabled={isSaving}
                                aria-label="Close"
                            >
                                <Icon name="close" />
                            </button>
                        </div>

                        <form onSubmit={handleSave}>
                            <div className="contacts-modal-body">
                                {formError && (
                                    <div className="contacts-alert contacts-alert-error">
                                        {formError}
                                    </div>
                                )}

                                <div className="contacts-form-group">
                                    <label htmlFor="contact-name">
                                        Name
                                    </label>

                                    <input
                                        id="contact-name"
                                        type="text"
                                        value={name}
                                        onChange={(event) =>
                                            setName(event.target.value)
                                        }
                                        placeholder="Enter full name"
                                        autoComplete="name"
                                        disabled={isSaving}
                                        autoFocus
                                    />
                                </div>

                                <div className="contacts-form-group">
                                    <label htmlFor="contact-email">
                                        Email address
                                    </label>

                                    <div className="contacts-input-with-icon">
                                        <span>
                                            <Icon
                                                name="email"
                                                size={17}
                                            />
                                        </span>

                                        <input
                                            id="contact-email"
                                            type="email"
                                            value={email}
                                            onChange={(event) =>
                                                setEmail(
                                                    event.target.value
                                                )
                                            }
                                            placeholder="name@example.com"
                                            autoComplete="email"
                                            disabled={isSaving}
                                        />
                                    </div>

                                    <small>
                                        This email is used to identify the
                                        participant when they sign in.
                                    </small>
                                </div>
                            </div>

                            <div className="contacts-modal-footer">
                                <button
                                    type="button"
                                    className="contacts-secondary-button"
                                    onClick={closeModal}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="contacts-primary-button"
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" />
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon
                                                name={
                                                    editing
                                                        ? "edit"
                                                        : "add"
                                                }
                                                size={17}
                                            />
                                            <span>
                                                {editing
                                                    ? "Save changes"
                                                    : "Add contact"}
                                            </span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsPage;